import React from "react";
import { useFormik } from "formik";
import { useRouter } from "next/navigation";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import emailValidator from "email-validator";
import { phone as phoneValidator } from "phone";
import { isEmpty, get, trim } from "lodash";
import { md5 } from "../../../core/utils/crypo-utils";
import { formatAuthProxyError } from "../../../core/utils/auth-utils";
import dynamic from "next/dynamic";
import styles from "../../index.module.scss";

const OtpInput = dynamic(() => import("react-otp-input"), { ssr: false });

interface OTPFormProps {
  userId: string;
  onBack?: () => void;
  onSuccess?: (data: any) => void;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  className?: string;
}

export default function OTPForm({ 
  userId, 
  onBack, 
  onSuccess, 
  loading = false, 
  setLoading = () => {},
  className = "",
}: OTPFormProps) {
  const router = useRouter();

  const setLocalData = (key: string, data: any) => {
    localStorage.setItem(md5(trim(key)), JSON.stringify(data));
  };

  const processPath = (type: string, data: any) => {
    let path = "";
    if (type === "customer") {
      path = "/customers";
    } else if (type === "vendor") {
      path = "/vendors";
    } else if (type === "admin") {
      path = "/admin";
    } else {
      path = "/login";
    }

    if (path) {
      router.push(path + "?&t=" + new Date().getTime().toString());
    }
  };

  const formik = useFormik({
    initialValues: {
      otp: "",
      email: "",
      phoneNumber: "",
    },
    
    onSubmit: async (values, formikHelpers) => {
      if (emailValidator.validate(userId)) {
        values.email = userId;
      }

      if (phoneValidator(userId).isValid) {
        values.phoneNumber = userId;
      }

      setLoading(true);
      const result = await fetch(`/api/auth/v2/otp/validate`, {
        method: "POST",
        body: JSON.stringify(values),
      });

      setLoading(false);

      if (result.ok) {
        const data = await result.json();
        if (data.header.statusCode == "OK") {
          //Check if user exists
          if (isEmpty(data.body.profile.userId)) {
            formikHelpers.setErrors({ otp: "No Account exists" });
            return;
          }
          const type = get(data, "body.profile.type");
          const { sec, userIdType } = data.body;
          setLocalData(userId, { sec, userIdType });
          
          if (onSuccess) {
            onSuccess(data);
          } else {
            processPath(type, {});
            if (window && window.location) {
              // if router did't work do a hard refresh
              // This is an existing but sometime rout.push not working
              setTimeout(() => {
                window.location.reload();
              }, 0);
            }
          }
        } else if (data.header.statusCode == "INACTIVE_ACCOUNT") {
          formikHelpers.setErrors({ otp: "Account is inactive" });
        }
      } else {
        const error = await result.json();
        formikHelpers.setErrors({
          otp: formatAuthProxyError(error, process.env.NEXT_PUBLIC_API_HOST),
        });
      }
    },

    validate: async (values) => {
      const errors: any = {};

      if (!values.otp) {
        errors.otp = "Enter OTP";
      }

      return errors.otp ? errors : undefined;
    },
  });

  const handleOTP = (v: any) => {
    const { setFieldValue } = formik;
    setFieldValue("otp", v);
  };

  return (
    <div className={`backdrop-blur-xl bg-white/95 rounded-3xl border border-white/30 p-8 shadow-2xl ${className}`}>
      {/* Form Header */}
      

      <div className="flex justify-center flex-col items-center mb-10">
        <div className={styles.logo}>
         
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Verify OTP</h2>
        <p className="text-gray-600 text-lg">Enter the code sent to your phone</p>
      </div>

      {/* OTP Form */}
      <form onSubmit={formik.handleSubmit} className="space-y-8">
        <div className="text-center">
          <div className="text-gray-700 mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            OTP sent to <span className="font-bold text-gray-900">{userId}</span>
            <Button
              onClick={onBack}
              className="ml-3 !text-blue-600 important hover:text-blue-700 text-sm font-medium transition-colors duration-200"
              size="small"
            >
              change
            </Button>
          </div>
          
          <OtpInput
            inputType={"number"}
            value={formik.values.otp}
            onChange={handleOTP}
            numInputs={6}
            renderSeparator={<span className="md:mx-2 text-blue-400 md:text-transparent">-</span>}
            shouldAutoFocus={true}
            skipDefaultStyles={true}
            inputStyle={
              Boolean(formik.errors.otp)
                ? "w-11 h-12 border-2 border-red-400 rounded-md text-center text-xl font-bold focus:border-blue-500 focus:outline-none bg-white text-gray-900 placeholder:text-gray-400 shadow-lg"
                : "w-11 h-12 border-2 border-blue-300 rounded-md text-center text-xl font-bold focus:border-blue-500 focus:outline-none bg-white text-gray-900 placeholder:text-gray-400 shadow-lg"
            }
            renderInput={(props) => <input {...props} />}
          />
          
          {formik.errors.otp && (
            <div className="text-red-600 text-sm mt-4 bg-red-50 border border-red-200 rounded-lg p-3">{formik.errors.otp}</div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          variant="contained"
          disableElevation
          type="submit"
          disabled={loading}
          size="large"
          fullWidth
          className="h-14 text-lg font-bold bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
          {loading ? (
            <CircularProgress size={24} className="text-white" />
          ) : (
            "Verify & Continue"
          )}
        </Button>
      </form>
    </div>
  );
}
